import React, { useState, useCallback } from "react";
import { useKeyPressEvent } from "../hooks";
import { EVENT_PRIORITIES } from "./shared";

interface Props {
  sourceId: number;
  currentPageNb: number;
  totalPageNumber: number;
  onChangePage: (page: number) => void;
}

const PaginationControls: React.FC<Props> = ({
  sourceId,
  currentPageNb,
  totalPageNumber,
  onChangePage,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(currentPageNb.toString());

  const goToPreviousPage = useCallback(
    () => onChangePage(Math.max(1, currentPageNb - 1)),
    [currentPageNb, totalPageNumber]
  );

  const goToNextPage = useCallback(
    () => onChangePage(Math.min(currentPageNb + 1, totalPageNumber)),
    [currentPageNb, totalPageNumber]
  );

  const handleKeyEvent = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "<") {
        e.preventDefault();
        goToPreviousPage();
      } else if (e.key === ">") {
        goToNextPage();
      }
    },
    [goToPreviousPage, goToNextPage]
  );

  const handlePageClick = () => {
    setIsEditing(true);
    setInputValue(currentPageNb.toString());
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    setIsEditing(false);
    //TODO: could have some kind of sign to user when they choose something out of range
    //- even just a flashing red border of the input
    const newPage = Math.max(1, Math.min(Number(inputValue), totalPageNumber));
    onChangePage(newPage);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleInputBlur();
    }
  };

  useKeyPressEvent(
    {
      id: `paged_source___${sourceId}`,
      priority: EVENT_PRIORITIES.YT_PAGE,
      useCallbackGeneratedCB: handleKeyEvent,
    },
    true
  );

  return (
    <div className="flex gap-6 font-bold">
      <button
        className="w-12 cursor-pointer border border-black"
        onClick={goToPreviousPage}
      >
        {"<"}
      </button>
      <div className="flex-none text-sm">
        <div className="flex gap-2 h-full">
          <div className="flex flex-col justify-center">
            {isEditing ? (
              <input
                type="number"
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onKeyDown={handleInputKeyDown}
                className="w-20 text-center text-lg border border-gray-300 rounded-md"
                onFocus={(e) => e.target.select()} // Highlight all text on focus
                autoFocus
              />
            ) : (
              <div>
                <span onClick={handlePageClick} className="cursor-pointer">
                  page {currentPageNb}{" "}
                </span>
              </div>
            )}
          </div>
          <div className="flex flex-col justify-center">
            <span>/ {totalPageNumber}</span>
          </div>
        </div>
      </div>
      <button
        className="w-12 cursor-pointer border border-black"
        onClick={goToNextPage}
      >
        {">"}
      </button>
    </div>
  );
};

export default PaginationControls;
